import React, { Component } from 'react';

import 'whatwg-fetch';

import App from '../App';
import Board from './Board';
import Constants from '../Constants';
import LetterOptionDisplay from './LetterOptionDisplay';
import MessagePanel from './MessagePanel';
import QuitPanel from './QuitPanel';
import RulesModal from './modals/RulesModal';
import ScheduleModal from './modals/ScheduleModal';
import Scoring from '../Scoring';
import Seasons from '../Seasons';
import SearchBar from './SearchBar';
import SummaryPanel from './SummaryPanel';
import Utils from '../Utils';

import '../styles/Game.css';

class Game extends Component {
  constructor(props) {
    super(props);

    const active = true;
    const loading = true;
    const showSummary = false;
    const holdCount = this.props.holdCount;
    const previewCount = this.props.previewCount;
    const expansions = this.props.expansions;
    const bonusCells = this.props.bonusCells;
    const elapsedTurns = this.props.elapsedTurns;
    const placedTiles = this.props.elapsedTurns + (this.props.startLetters ? Object.keys(this.props.startLetters).length : 0);
    const gridArray = this.props.boardString
      ? Scoring.expandGridString(this.props.boardString, this.props.rows, this.props.columns)
      : Scoring.createEmptyGridArray(this.props.rows, this.props.columns);
    const startLetters = this.props.startLetters;
    // Only pre-populate start letters if it's a new game. Spectated games have
    // 0x0 dimensions at this point, and a resumed game already carries them in
    // its board -- stamping them again is a no-op on every other day, but on a
    // slide day they have moved, so it would write them back where they started
    // and overwrite whatever slid into their place.
    if (this.props.startLetters && this.props.rows > 0 && !this.props.boardString) {
      Object.keys(this.props.startLetters).forEach((coordinates) => {
        const [row, column] = coordinates.split(",");
        gridArray[row][column] = this.props.startLetters[coordinates];
      });
    }

    const { rowData, columnData, totalScore } =
      Scoring.calculateBoardData(gridArray, this.props.expansions, this.props.bonusCells, this.props.wraparoundMode);
    const roundLetterOptions = Array(this.props.letterOptionsPerRound).fill(null);
    const selectedLetterIndex = -1;
    const heldLetters = Array(this.props.holdCount).fill(null);
    for (let i = 0; i < this.props.holdString.length; i++) {
      heldLetters[i] = this.props.holdString.charAt(i);
    }
    const selectedHoldIndex = -1;
    const previewLetters = Array(this.props.previewCount).fill(null);
    // What this board does differently, one entry per mode or variant in play,
    // each named. A list rather than one concatenated string for two reasons:
    // the name has to be emboldened, which a string cannot carry, and a game can
    // genuinely be several of these at once -- free play offers the modes as
    // independent checkboxes, so an else-if chain silently dropped every
    // explanation but the first. Null when there is nothing to say, since the
    // panel is rendered on the truthiness of this and an empty array is truthy.
    //
    // A spectator is shown a finished board rather than told how to play it, so
    // they get none of these. One check over all six: a spectator was already
    // given none of them, but by two separate mechanisms -- the cell variants
    // tested `spectating` here, while the modes relied on SPECTATOR_PARAMETERS
    // having turned them off. That is one rule enforced in two places, only one
    // of which says so.
    const explanations = [];
    if (!this.props.spectating) {
      if (this.props.adjacencyMode) {
        explanations.push({ name: Constants.MODE_NAME_ADJACENCY, text: Constants.MESSAGE_ADJACENCY_MODE });
      }
      if (this.props.incrementMode) {
        explanations.push({
          name: Constants.MODE_NAME_INCREMENT,
          text: Utils.assembleIncrementModeString(this.props.letterOptionsPerRound)
        });
      }
      if (this.props.dropMode) {
        explanations.push({ name: Constants.MODE_NAME_DROP, text: Constants.MESSAGE_DROP_MODE });
      }
      if (this.props.slideMode) {
        explanations.push({ name: Constants.MODE_NAME_SLIDE, text: Constants.MESSAGE_SLIDE_MODE });
      }
      if (this.props.wraparoundMode) {
        explanations.push({ name: Constants.MODE_NAME_WRAPAROUND, text: Constants.MESSAGE_WRAPAROUND_MODE });
      }
      if (this.props.expansions.length > 0) {
        explanations.push({ name: Constants.VARIANT_NAME_EXPANDED, text: Constants.MESSAGE_EXPANDED });
      }
      if (this.props.bonusCells.length > 0) {
        explanations.push({ name: Constants.VARIANT_NAME_BONUS_CELLS, text: Constants.MESSAGE_BONUS_CELLS });
      }
    }

    const message = explanations.length > 0 ? explanations : null;
    const searchMessage = null;
    const saveError = null;
    const visibleModal = Constants.MODAL_NONE;

    this.state = {
      active,
      loading,
      showSummary,
      startLetters,
      holdCount,
      previewCount,
      placedTiles,
      gridArray,
      expansions,
      bonusCells,
      rowData,
      columnData,
      totalScore,
      roundLetterOptions,
      selectedLetterIndex,
      heldLetters,
      selectedHoldIndex,
      previewLetters,
      message,
      searchMessage,
      saveError,
      elapsedTurns,
      visibleModal,
    };
  }

  async componentDidMount() {
    if (!this.props.daily) {
      this.recordAttempt();
      const letterOptionString = await this.fetchLetterOptionString();
      this.setState({
        roundLetterOptions: this.getRoundLetterOptions(letterOptionString, this.props.elapsedTurns),
        previewLetters: this.getPreviewLetterOptions(letterOptionString, this.props.elapsedTurns),
        loading: false
      });

      return;
    }

    if (this.props.spectating) {
      return fetch(`/api/games/date/${this.props.date}/player/${this.props.spectating}`)
        .then(data => data.json())
        .then(async (res) => {
          if (res.data.length >= 1) {
            const rows = res.data[0].rowData.length;
            const columns = res.data[0].columnData.length;
            this.setState({
              active: false,
              loading: false,
              showSummary: true,
              startLetters: res.data[0].startLetters,
              expansions: res.data[0].expansions,
              bonusCells: res.data[0].bonusCells || [],
              holdCount: res.data[0].holdCount,
              previewCount: res.data[0].previewCount,
              placedTiles: rows * columns,
              gridArray: Scoring.expandGridString(res.data[0].grid, rows, columns),
              rowData: res.data[0].rowData,
              columnData: res.data[0].columnData,
              totalScore: res.data[0].totalScore,
              holdPenalty: res.data[0].holdPenalty,
              roundLetterOptions: [],
              selectedLetterIndex: -1
            });
          }
        });
    }

    // A daily that opens without its letters is a board that cannot be played,
    // so it says so rather than showing a grid of blank tiles and no reason.
    const letterOptionString = await this.fetchLetterOptionString();
    this.setState({
      roundLetterOptions: this.getRoundLetterOptions(letterOptionString, this.props.elapsedTurns),
      previewLetters: this.getPreviewLetterOptions(letterOptionString, this.props.elapsedTurns),
      saveError: letterOptionString ? null : Constants.ERROR_LETTERS_NOT_RECEIVED,
      loading: false
    });
  }

  componentDidUpdate() {
    if (this.state.active && this.state.placedTiles >= this.props.rows * this.props.columns + this.props.expansions.length) {
      this.endGame();
    }
  }

  isExpanded = (row, column) => {
    return Scoring.isExpanded(this.props.expansions, row, column);
  }

  isFull = (row, column) => {
    return this.state.gridArray[row][column].length === 2 || (!this.isExpanded(row, column) && this.state.gridArray[row][column].length === 1);
  }

  checkAdjacency = (row, column) => {
    if (row > 0 && this.isFull(row - 1, column)) {
      return true;
    }
    if (row + 1 < this.props.rows && this.isFull(row + 1, column)) {
      return true;
    }
    if (column > 0 && this.isFull(row, column - 1)) {
      return true;
    }
    if (column + 1 < this.props.columns && this.isFull(row, column + 1)) {
      return true;
    }
    if (this.isExpanded(row, column) && this.state.gridArray[row][column].length === 1) {
      return true;
    }

    return false;
  }

  handleCellSelection = async (row, column) => {
    if (!this.props.gameKey || this.state.loading) {
      return;
    }

    // On a slide day the only move is a push from an edge. Without this a cell
    // click places a letter mid-board: the daily would have it rejected and
    // rolled back by `findSlide`, but free play validates nothing, so the cells
    // were a way to opt out of the mode altogether.
    //
    // Answered with its own message rather than with the day's rule, which is
    // what the board says on load when nothing has gone wrong. A player who has
    // just clicked the wrong thing needs telling where to click instead.
    if (this.props.slideMode) {
      this.setState({ message: Constants.MESSAGE_USE_THE_ARROWS });
      return;
    }

    if (this.props.dropMode) {
      row = this.props.rows - 1;
      while (row >= 0 && this.isFull(row, column)) {
        row--;
      }

      if (row < 0) {
        this.setState({
          message: Constants.MESSAGE_COLUMN_FULL
        })
        return;
      }
    }

    if (this.state.gridArray[row][column].length >= (this.props.expansions.includes("" + row + "," + column) ? 2 : 1)) {
      this.setState({
        message: Constants.MESSAGE_CELL_ALREADY_OCCUPIED
      })
      return;
    }

    if (this.props.adjacencyMode && this.state.placedTiles > 0 && !this.checkAdjacency(row, column)) {
      this.setState({
        message: Constants.MESSAGE_CELL_NOT_ADJACENT
      })
      return;
    }

    const heldLetters = this.state.heldLetters.slice();
    const selectedLetter = this.takeSelectedLetter(heldLetters);
    if (!selectedLetter) {
      return;
    }

    this.setState({
      loading: true
    });

    const gridArray = this.state.gridArray.slice();
    gridArray[row][column].push(selectedLetter);

    const rowData = this.state.rowData.slice();
    rowData[row] = Scoring.calculateDataForStringArray(
      Scoring.getRowStringArray(gridArray, row, this.props.expansions),
      Scoring.getRowBonusIndexes(gridArray, row, this.props.bonusCells),
      this.props.wraparoundMode);

    const columnData = this.state.columnData.slice();
    columnData[column] = Scoring.calculateDataForStringArray(
      Scoring.getColumnStringArray(gridArray, column, this.props.expansions),
      Scoring.getColumnBonusIndexes(gridArray, column, this.props.bonusCells),
      this.props.wraparoundMode);

    const totalScore = Utils.calculateTotalScore(rowData, columnData);

    // The letter went into an array `state` is still holding, so undoing this
    // move means taking it back out again.
    await this.commitPlacement({
      gridArray, heldLetters, rowData, columnData, totalScore,
      rollback: () => gridArray[row][column].pop()
    });
  }

  // The letter this turn will use, taken from the round's options or from a
  // hold, which the caller's copy of `heldLetters` is emptied of. Null when
  // there is nothing selected, having said so.
  takeSelectedLetter = (heldLetters) => {
    if (this.state.selectedLetterIndex > -1) {
      const selectedLetter = this.state.roundLetterOptions[this.state.selectedLetterIndex];
      if (!selectedLetter) {
        this.setState({ message: Constants.MESSAGE_NO_LETTER_SELECTED });
        return null;
      }

      return selectedLetter;
    }

    if (this.state.selectedHoldIndex > -1) {
      const selectedLetter = heldLetters[this.state.selectedHoldIndex];
      if (!selectedLetter) {
        this.setState({ message: Constants.MESSAGE_NO_LETTER_SELECTED });
        return null;
      }

      heldLetters[this.state.selectedHoldIndex] = null;
      return selectedLetter;
    }

    this.setState({ message: Constants.MESSAGE_NO_LETTER_SELECTED });
    return null;
  }

  // A letter pushed in from an edge. The whole board is rescored rather than
  // the one row and column a dropped letter touches: a push carries a run of
  // letters along with it, so every line that run crosses can change.
  handleSlide = async (edge) => {
    if (!this.props.gameKey || this.state.loading) {
      return;
    }

    const heldLetters = this.state.heldLetters.slice();
    const selectedLetter = this.takeSelectedLetter(heldLetters);
    if (!selectedLetter) {
      return;
    }

    const shift = Scoring.getSlideShift(this.state.gridArray, edge);
    const gridArray = Scoring.applySlide(this.state.gridArray, edge, selectedLetter);
    if (!gridArray) {
      this.setState({ message: Constants.MESSAGE_LINE_FULL });
      return;
    }

    this.setState({ loading: true });

    // The day's given letters are marked by coordinate, and a push moves the
    // coordinate: without this the circle stays behind on a cell the letter has
    // left, and lands on whatever slid into its place.
    const startLetters = {};
    Object.keys(this.state.startLetters || {}).forEach((coordinates) => {
      startLetters[shift[coordinates] || coordinates] = this.state.startLetters[coordinates];
    });

    const { rowData, columnData, totalScore } =
      Scoring.calculateBoardData(gridArray, this.props.expansions, this.props.bonusCells, this.props.wraparoundMode);

    // `applySlide` built a fresh board and left the one in state alone, so a
    // rejected slide has nothing to undo.
    await this.commitPlacement({
      gridArray, startLetters, heldLetters, rowData, columnData, totalScore, rollback: () => {}
    });
  }

  // Everything a placement does once the board has been worked out, whether the
  // letter was dropped into a cell or pushed in from an edge.
  commitPlacement = async ({ gridArray, startLetters, heldLetters, rowData, columnData, totalScore, rollback }) => {
    const placedTiles = this.state.placedTiles + 1;
    const elapsedTurns = this.state.elapsedTurns + 1;
    const attempt = this.props.daily ? await this.recordAttempt(gridArray, elapsedTurns, heldLetters) : null;
    if (this.props.daily && !attempt) {
      // The server did not accept the move, so it never happened: without its
      // record the next round's letters are withheld and the game would stall.
      rollback();
      this.setState({
        loading: false,
        saveError: Constants.ERROR_TURN_NOT_RECORDED
      });

      // Client and server now disagree about the board -- the save may well have
      // landed and only its response been lost -- so rebuild from the server's
      // record rather than carrying on from a guess.
      this.props.onSaveFailure();
      return;
    }

    // The coming round's letters rode back on the response that recorded the
    // move, so the turn costs one request rather than the three it used to. The
    // fetch is still there as a fallback: a response without them would
    // otherwise leave the player looking at blank tiles with no way forward, and
    // being merely as slow as this used to be is a far better failure.
    let letterOptionString = this.props.daily ? attempt.letterOptions : this.props.letterOptionString;
    if (!letterOptionString) {
      letterOptionString = await this.fetchLetterOptionString();
    }

    if (this.props.daily && !letterOptionString) {
      // The move itself was recorded -- only its letters went missing -- so the
      // letter stays on the board and nothing is rolled back. Resuming from the
      // server's record remounts the board and fetches the round afresh, which
      // is the whole of the recovery; the message is for when that fails too.
      this.setState({
        loading: false,
        saveError: Constants.ERROR_LETTERS_NOT_RECEIVED
      });
      this.props.onSaveFailure();
      return;
    }

    const roundLetterOptions = this.getRoundLetterOptions(letterOptionString, elapsedTurns);
    const previewLetters = this.getPreviewLetterOptions(letterOptionString, elapsedTurns);
    const selectedLetterIndex = -1;
    const selectedHoldIndex = -1;
    const message = null;
    const searchMessage = null;
    const saveError = null;
    const loading = false;

    this.setState({
      loading,
      placedTiles,
      gridArray,
      // Only a slide moves these; every other placement leaves them alone.
      startLetters: startLetters || this.state.startLetters,
      rowData,
      columnData,
      totalScore,
      roundLetterOptions,
      selectedLetterIndex,
      heldLetters,
      previewLetters,
      selectedHoldIndex,
      message,
      searchMessage,
      saveError,
      elapsedTurns
    });
  }

  handleLetterOptionSelection = (selectedLetterIndex) => {
    this.setState({
      selectedLetterIndex,
      selectedHoldIndex: -1
    });
  }

  handleLetterHold = (selectedHoldIndex) => {
    if (!this.props.gameKey || !this.state.active) {
      return;
    }

    if (this.state.heldLetters[selectedHoldIndex]) {
      return;
    }

    const roundLetterOptions = this.state.roundLetterOptions.slice();

    const selectedLetter = roundLetterOptions[this.state.selectedLetterIndex];
    const heldLetters = this.state.heldLetters.slice();
    heldLetters[selectedHoldIndex] = selectedLetter;
    roundLetterOptions[this.state.selectedLetterIndex] = null;

    this.setState({
      roundLetterOptions,
      heldLetters
    });
  }

  handleHeldLetterSelection = (selectedHoldIndex) => {
    this.setState({
      selectedLetterIndex: -1,
      selectedHoldIndex
    });
  }

  handleQuitButtonClick = () => {
    if (this.props.daily && this.state.active && this.props.valid) {
      this.recordQuit();
    }

    this.props.onQuitButtonClick();
  }

  handleSearchSubmit = (query) => {
    const queryUpper = query.toUpperCase();
    let searchMessage = `${queryUpper} is not a valid word. ✗`;

    if (Scoring.isWord(query)) {
      searchMessage = `${queryUpper} is a valid word. ✓`;
    }

    this.setState({
      searchMessage
    });
  }

  handleRulesButtonClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_RULES
    });
  }

  // The rules modal's way into the schedule. Game keeps its own modals rather
  // than App's, so the schedule opens here, in place of the rules.
  handleScheduleClick = () => {
    this.setState({
      visibleModal: Constants.MODAL_SCHEDULE
    });
  }

  handleModalClose = () => {
    this.setState({
      visibleModal: Constants.MODAL_NONE
    });
  }

  // The letters revealed so far, as one string: fetched for a daily, handed over
  // up front for free play. A turn needs two slices of it -- this round's options
  // and, on preview days, the next round's -- so it is fetched once and sliced
  // twice, rather than fetched once per slice as it used to be.
  //
  // Returns null rather than throwing. Every caller awaits this on its way to
  // clearing the spinner, so a rejection here left `loading` set for good: a
  // board frozen mid-turn, on a move the server had very possibly recorded. The
  // callers surface the null instead, which is recoverable.
  fetchLetterOptionString = async () => {
    if (!this.props.daily) {
      return this.props.letterOptionString;
    }

    try {
      const dailyGameData = await App.fetchDailyGameData();
      return dailyGameData ? dailyGameData.gameData.letterOptions : null;
    } catch (error) {
      console.log("Failed to fetch letter options: " + error.message);
      return null;
    }
  }

  // One round's letters out of the revealed string. The string is laid out in
  // fixed blocks -- three wide on an increment day, however few of them that
  // round is showing -- so a round is found by multiplying, and how much of its
  // block is on offer is `Seasons.getRoundLetterOptionCount`. That rule is
  // shared with the server, which holds the rest of the block back and refuses
  // a letter taken from it.
  getRoundLetterOptions = (letterOptionString, elapsedTurns) => {
    if (!letterOptionString) {
      return Array(this.props.letterOptionsPerRound).fill(null);
    }

    const blockWidth = this.props.incrementMode
      ? Constants.MAX_LETTER_OPTIONS_PER_ROUND
      : this.props.letterOptionsPerRound;
    const startIndex = blockWidth * elapsedTurns;
    if (startIndex >= letterOptionString.length) {
      return [];
    }

    const letterOptionCount = Seasons.getRoundLetterOptionCount(this.props, elapsedTurns);

    return letterOptionString.slice(startIndex, startIndex + letterOptionCount).split("").sort();
  }

  // The next round's letters, on a day that grants a preview. The server sends
  // exactly as many of them as the day grants: it used to send the whole round
  // and leave the client to show a few, which left the rest in the response for
  // anyone who cared to look. So this shows what it was given rather than
  // drawing its own subset -- which also settles the letters, where a subset
  // drawn here was redrawn every time the board remounted.
  //
  // The count is applied here as well as there, and has to be: a free play
  // board holds the whole of its own string, so nothing is being held back from
  // it and the cut is the client's to make. On a daily it is a second cut of a
  // string already cut to the same size.
  //
  // Blanks stand in where there is no next round to preview, so the row keeps
  // its height through the last turn of the board.
  getPreviewLetterOptions = (letterOptionString, elapsedTurns) => {
    if (this.props.previewCount <= 0) {
      return [];
    }

    const letterOptions = letterOptionString
      ? this.getRoundLetterOptions(letterOptionString, elapsedTurns + 1).slice(0, this.props.previewCount)
      : [];

    return letterOptions.length > 0 ? letterOptions : Array(this.props.previewCount).fill(null);
  }

  endGame = async () => {
    this.setState({
      active: false,
      showSummary: true
    });

    if (this.props.daily && !(await this.recordGame())) {
      this.setState({
        saveError: Constants.ERROR_GAME_NOT_RECORDED
      });
    }

    this.props.onGameFinish();
  }

  recordGame = async () => {
    // Only the board and the name to check it against are sent: the server
    // re-derives the day's parameters, rescores the grid, and decides whether the
    // game counts towards the leaderboard. `playerName` is not what the game is
    // recorded under -- that has always come from the token -- it is only there
    // for the server to notice that this client thinks it is somebody else.
    const grid = Scoring.compressGridArray(this.state.gridArray, this.props.expansions);
    const playerName = this.props.playerName;

    try {
      const data = await fetch('/api/games', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grid, playerName }),
      });
      const dataJson = await data.json();
      if (!dataJson.success) {
        console.log("Failed to record game: " + dataJson.error);
        return false;
      }
    } catch (error) {
      console.log("Failed to record game: " + error.message);
      return false;
    }

    return true;
  }

  recordAttempt = async (gridArray, elapsedTurns, heldLetters) => {
    const type = this.props.daily ? Constants.GAME_TYPE_DAILY : Constants.GAME_TYPE_PRACTICE;
    const playerName = this.props.playerName;
    const date = this.props.date;
    const letterOptions = this.props.letterOptionsPerRound;
    const holdCount = this.props.holdCount;
    const previewCount = this.props.previewCount;
    const adjacencyMode = this.props.adjacencyMode;
    const incrementMode = this.props.incrementMode;
    const dropMode = this.props.dropMode;
    const slideMode = this.props.slideMode;
    const boardString = Scoring.compressGridArray(gridArray || this.state.gridArray, this.props.expansions);
    const holdString = (heldLetters || this.state.heldLetters).map(letter => letter || "").join("");
    elapsedTurns = elapsedTurns === undefined ? this.state.elapsedTurns : elapsedTurns;

    try {
      const data = await fetch('/api/attempts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, playerName, date, letterOptions, holdCount, previewCount, adjacencyMode, incrementMode, dropMode, slideMode, boardString, elapsedTurns, holdString }),
      });
      const dataJson = await data.json();
      if (!dataJson.success) {
        console.log("Failed to record attempt: " + dataJson.error);
        return null;
      }

      // A daily's response carries the letters this placement has just earned;
      // free play generated its whole sequence up front and has none to hand back.
      const gameData = dataJson.data && dataJson.data.gameData;
      return { letterOptions: gameData ? gameData.letterOptions : null };
    } catch (error) {
      console.log("Failed to record attempt: " + error.message);
      return null;
    }
  }

  recordQuit = async () => {
    const playerName = this.props.playerName;
    const date = this.props.date;

    const data = await fetch('/api/quits', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerName, date }),
    });
    const dataJson = await data.json();
    if (!dataJson.success) console.log("Failed to record quit.");
  }

  calculateHoldPenalty = () => {
    return this.state.heldLetters.reduce((acc, cur) => { return cur ? acc + Constants.PENALTY_HOLD : acc }, 0);
  }

  render() {
    return (
      <div className="game">
        <Board
          gridArray={this.state.gridArray}
          startLetters={this.state.startLetters}
          expansions={this.state.expansions}
          bonusCells={this.state.bonusCells}
          loading={this.state.loading}
          rowData={this.state.rowData}
          columnData={this.state.columnData}
          slideMode={this.props.slideMode}
          onCellSelection={this.handleCellSelection}
          onSlide={this.handleSlide}
        />
        { this.state.active &&
          <LetterOptionDisplay
            letterOptions={this.state.roundLetterOptions}
            selectedLetterIndex={this.state.selectedLetterIndex}
            selectedHoldIndex={this.state.selectedHoldIndex}
            heldLetters={this.state.heldLetters}
            previewLetters={this.state.previewLetters}
            onLetterHold={this.handleLetterHold}
            onHeldLetterSelection={this.handleHeldLetterSelection}
            onLetterOptionSelection={this.handleLetterOptionSelection}
          />
        }
        { this.state.message &&
          <MessagePanel message={this.state.message} />
        }
        { this.state.saveError &&
          <div className="game-error">
            {this.state.saveError}
          </div>
        }
        { this.state.showSummary &&
          <SummaryPanel
            score={this.state.totalScore}
            spectating={this.props.spectating}
            holdPenalty={this.calculateHoldPenalty()}
            showBreakdown={this.state.holdCount > 0}
          />
        }
        { this.state.active &&
          <SearchBar
            message={this.state.searchMessage}
            onSubmit={this.handleSearchSubmit}
          />
        }
        { !this.props.spectating &&
          <button className="button-rules mobile-and-tablet-only" onClick={this.handleRulesButtonClick}>
            Rules
          </button>
        }
        { (!this.props.daily || !this.state.active || !this.props.valid) &&
          <QuitPanel onQuitButtonClick={this.handleQuitButtonClick} />
        }
        { this.state.visibleModal === Constants.MODAL_RULES &&
          <RulesModal onScheduleClick={this.handleScheduleClick} onClose={this.handleModalClose}/>
        }
        { this.state.visibleModal === Constants.MODAL_SCHEDULE &&
          <ScheduleModal onClose={this.handleModalClose} />
        }
      </div>
    );
  }
}

export default Game;
